import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, CssBaseline, Drawer as MuiDrawer, Button } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PublishIcon from '@mui/icons-material/Publish';
import logo from "../assets/images/logo.png";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseconf';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';

const drawerWidth = 260;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
    },
});

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    [theme.breakpoints.down('md')]: {
        marginLeft: 0,
        width: '100%',
    },
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end', // Align the button to the end
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

export default function Layout({ children }) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(!isSmallScreen);
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        const storedUser = sessionStorage.getItem('logged_user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            setUserRole(user.role);
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleLogout = () => {
        sessionStorage.removeItem('logged_user');
        sessionStorage.removeItem('user_role');
        signOut(auth)
            .then(() => {
                navigate('/login');
            })
            .catch((error) => {
                console.error('Logout error:', error);
            });
    };

    const teacherLinks = [
        { text: 'Dashboard', icon: <DashboardIcon />, href: '/teacher-dashboard' },
        { text: 'Check Assignments', icon: <FactCheckIcon />, href: '/check-assignment' },
        { text: 'View Assignments', icon: <ReceiptIcon />, href: '/assignment' },
        { text: 'Add Students', icon: <PersonAddAlt1Icon />, href: '/studentform' },
        { text: 'Edit Students', icon: <EditIcon />, href: '/edit-student' },
        { text: 'Remove Students', icon: <PersonRemoveAlt1Icon />, href: '/remove-student' },
        { text: 'Update Marks', icon: <AssignmentIcon />, href: '/updatemarks' },
        { text: 'Update Attendance', icon: <AccessTimeIcon />, href: '/addAttendence' },
    ];

    const studentLinks = [
        { text: 'Dashboard', icon: <DashboardIcon />, href: '/dashboard' },
        { text: 'View Sessionals Marks', icon: <DescriptionIcon />, href: '/check-sessional' },
        { text: 'View Final Marks', icon: <PercentIcon />, href: '/checkmarks' },
        { text: 'Submit Assignment', icon: <PublishIcon />, href: '/submit-assignment' },
        { text: 'Check Attendance', icon: <EventAvailableIcon />, href: '/check-attendence' },
    ];

    const linksToShow = userRole === 'teacher' ? teacherLinks : studentLinks;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <CssBaseline />

            <AppBar position="fixed" className="bg-white text-black" open={open}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{
                                marginRight: 2,
                                ...(open && isSmallScreen && { display: 'none' }),
                                color: '#000',
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src={logo}
                            alt="Logo"
                            sx={{
                                height: 50,
                                width: 'auto',
                                '@media (max-width: 600px)': {
                                    height: 40,
                                },
                            }}
                        />
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                fontWeight: 600,
                                color: '#000',
                                display: { xs: 'none', sm: 'block' },
                                marginLeft: 2,
                            }}
                        >
                            Dashboard
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {open && isSmallScreen && (
                            <IconButton color="inherit" onClick={handleDrawerClose} sx={{ color: '#000' }}>
                                <ChevronLeftIcon />
                            </IconButton>
                        )}
                        <IconButton color="inherit" sx={{ color: '#000' }}>
                            <AccountCircleIcon />
                        </IconButton>
                        <Button
                            color="inherit"
                            startIcon={<LogoutIcon />}
                            onClick={handleLogout}
                            sx={{
                                display: { xs: 'none', sm: 'flex' },
                                color: '#000',
                                textTransform: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            Logout
                        </Button>
                        <IconButton
                            color="inherit"
                            onClick={handleLogout}
                            sx={{
                                display: { xs: 'flex', sm: 'none' },
                                color: '#000',
                            }}
                            title="Logout"
                        >
                            <LogoutIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {isSmallScreen ? (
                <MuiDrawer
                    anchor="left"
                    variant="temporary"
                    open={open}
                    onClose={handleDrawerClose}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            marginTop: '64px',
                        },
                    }}
                >
                    <DrawerHeader sx={{ justifyContent: 'flex-start', pl: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Menu
                        </Typography>
                    </DrawerHeader>
                    <Divider />
                    <List sx={{ p: 0 }}>
                        {linksToShow.map((link, index) => (
                            <ListItem
                                button
                                key={index}
                                component="a"
                                href={link.href}
                                onClick={handleDrawerClose}
                                sx={{
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40, color: '#1976d2' }}>
                                    {link.icon}
                                </ListItemIcon>
                                <ListItemText primary={link.text} />
                            </ListItem>
                        ))}
                    </List>
                </MuiDrawer>
            ) : (
                <Drawer variant="permanent" open={open}>
                    <DrawerHeader />
                    <Divider />
                    <List sx={{ p: 0 }}>
                        {linksToShow.map((link, index) => (
                            <ListItem
                                button
                                key={index}
                                component="a"
                                href={link.href}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    justifyContent: open ? 'flex-start' : 'center',
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: open ? 40 : 'auto', color: '#1976d2' }}>
                                    {link.icon}
                                </ListItemIcon>
                                {open && <ListItemText primary={link.text} />}
                            </ListItem>
                        ))}
                    </List>
                </Drawer>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2, md: 3 },
                    backgroundColor: '#f5f5f5',
                    minHeight: '100vh',
                    marginTop: '64px',
                    overflow: 'auto',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}







